import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import type { BoardTask } from './useBoardTasks';

interface CourseNote {
  id: string;
  title: string;
  content: string | null;
  status: string;
  priority: string | null;
  course_id: string | null;
  lesson_id: string | null;
  assigned_to: string | null;
  created_by: string;
  due_date: string | null;
  created_at: string | null;
  updated_at: string | null;
}

/**
 * Maps course_notes to BoardTask-compatible objects for unified display in the Planungszentrale.
 * These are read-only in the planning views.
 */
export function useCourseNotesAsBoardTasks() {
  return useQuery({
    queryKey: ['course-notes-as-board-tasks'],
    queryFn: async () => {
      // Offene UND erledigte Kurs-Notizen laden, damit sie korrekt in
      // der "Erledigt"-Spalte landen statt vom Board zu verschwinden.
      const { data, error } = await supabase
        .from('course_notes')
        .select('id, title, content, status, priority, course_id, lesson_id, assigned_to, created_by, due_date, created_at, updated_at')
        .in('status', ['offen', 'erledigt'])
        .order('created_at', { ascending: false });

      if (error) throw error;

      // Fetch course titles for context
      const courseIds = [...new Set((data || []).map(n => n.course_id).filter(Boolean))] as string[];
      let courseTitles: Record<string, string> = {};
      
      if (courseIds.length > 0) {
        const { data: courses } = await supabase
          .from('courses')
          .select('id, title')
          .in('id', courseIds);
        
        if (courses) {
          courseTitles = Object.fromEntries(courses.map(c => [c.id, c.title]));
        }
      }

      // Map to BoardTask format
      return (data || []).map((note): BoardTask & { _source: 'course_note'; _courseTitle?: string } => ({
        id: `cn_${note.id}`,
        title: note.title,
        description: note.content,
        // Erledigte Notizen erscheinen in der "done"-Spalte, offene in "course_tasks"
        status: note.status === 'erledigt' ? 'done' : 'course_tasks',
        priority: note.priority === 'hoch' ? 'high' : note.priority === 'niedrig' ? 'low' : 'normal',
        category: 'Kurs-Aufgabe',
        area: 'Lernplattform',
        assigned_to: note.assigned_to,
        created_by: note.created_by,
        sort_order: null,
        created_at: note.created_at || new Date().toISOString(),
        updated_at: note.updated_at || new Date().toISOString(),
        due_date: note.due_date,
        start_date: null,
        linked_entity_type: note.course_id ? 'course' : null,
        linked_entity_id: note.course_id,
        linked_page_path: note.course_id
          ? `/admin/dashboard/kurse/${note.course_id}${note.lesson_id ? `?lektion=${note.lesson_id}` : ''}`
          : null,
        milestone: false,
        color_label: 'orange',
        attachments: (note as Record<string, unknown>).attachments as BoardTask['attachments'] ?? null,
        ausgeblendet_fuer: [],
        _source: 'course_note' as const,
        _courseTitle: note.course_id ? courseTitles[note.course_id] : undefined,
      }));
    },
  });
}
